import dayjs from "dayjs";
import type { QueryModelPosition } from "../../api/queries/types";
import type { Contact, Portfolio } from "../../api/types";
import type { PendingOrder } from "../forms/quality/suitability/SuitabilityForm.types";
import type { SuitOrderInput } from "./suitOrderTypes";
import type { Section51Variant, SuitStatementData } from "./suitStatementData";
import { roundNumber } from "@/utils/number";

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

/** Flat map that can carry profile values from either source. */
type ProfileMap = Record<string, string | number | boolean | null | undefined>;

/** Flat map of UI test-parameter overrides (always strings). */
type TestOverrides = Record<string, string>;

function clampFraction(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export const investmentHorizonTag = "investmentHorizon";
export const liquidationTimeTag = "liquidationTime";

// ---------------------------------------------------------------------------
// Module-level helpers
// ---------------------------------------------------------------------------

function resolveModelDescription(
  modelType: string,
  modelTypeName?: string
): string {
  const provided = (modelTypeName ?? "").trim();
  const mapped = MODEL_DESCRIPTIONS[modelType] ?? "";

  if (provided && provided.toUpperCase() !== "NOMODEL") {
    // If upstream sends model code as "name", prefer mapped display text.
    if (provided === modelType && mapped) return mapped;
    return provided;
  }

  return mapped || modelType;
}

function resolveListPrice(amount: number, modelType: string): number {
  if (modelType === "NORDNET") return 0.6;
  if (modelType === "CAPGUARDTRYGG") return 0.25;
  if (modelType === "FONDSOBLIGASJON") return 0.5;
  if (modelType === "CAPGUARDRENTER") {
    if (amount < 2_000_000) return 1.0;
    if (amount < 5_000_000) return 0.8;
    if (amount < 10_000_000) return 0.7;
    return 0.5;
  }
  if (modelType === "CAPGUARDEKB" || modelType === "CAPGUARDEIENDOM") {
    if (amount < 2_000_000) return 1.2;
    if (amount < 5_000_000) return 1.1;
    if (amount < 10_000_000) return 1.0;
    return 0.9;
  }
  if (amount < 2_000_000) return 1.2;
  if (amount < 5_000_000) return 1.0;
  if (amount < 10_000_000) return 0.9;
  return 0.8;
}

const MODEL_DESCRIPTIONS: Record<string, string> = {
  NORDNET: "Nordnet",
  CAPGUARDRENTER: "Aktiv Renter",
  CAPGUARDTRYGG: "Trygg",
  CAPGUARDEKB: "Aktiv Egenkapitalbevis",
  CAPGUARDEIENDOM: "Aktiv Eiendom",
  FONDSOBLIGASJON: "Fondsobligasjoner",
  CAPGUARDAKSJER: "Aktiv Aksjer",
  CAPGUARD75: "Aktiv 75/25",
  CAPGUARD60: "Aktiv 60/40",
  CAPGUARD45: "Aktiv 45/55",
  CAPGUARD25: "Aktiv 25/75",
};

function computeOrderFeeRow(
  order: PendingOrder,
  modelsData: QueryModelPosition[]
): {
  portCost: number;
  portCostInclAccountFee: number;
  portClean: number;
  portManFee: number;
  portManKick: number;
  portTotFee: number;
  actAccountFee: number;
  actPerfFee: number | null;
} {
  const modelType = order.modelType ?? "";
  const isModel = !!modelType && modelType !== "NOMODEL";
  const CAP_PORT_TYPES = ["20", "21", "22", "23", "24"];
  const isCap = CAP_PORT_TYPES.includes(order.portType ?? "");
  const isAsk = order.portType === "22";
  const accountFee = isCap ? (isAsk ? 0.15 : 0.18) : 0.3;
  const amount =
    (order.amountToInvest ?? 0) +
    (order.oneOffAmount ?? 0) -
    (order.withdrawal ?? 0);
  const rawFee = order.managementFeePct ?? 0;
  const effectiveFee =
    rawFee === 1.5 ? resolveListPrice(amount, modelType) : rawFee;
  const r = (n: number) => Math.round(n * 100) / 100;

  if (isModel) {
    const positions = modelsData.filter((p) => p.short_name === modelType);
    const totalShares = positions.reduce((s, p) => s + p.share_of_portfolio, 0);
    let totalManFee = 0;
    let totalKick = 0;
    for (const pos of positions) {
      const share = totalShares > 0 ? pos.share_of_portfolio / totalShares : 0;
      totalManFee += pos.management_fee_percentage * share;
      totalKick +=
        (pos.kickback_percentage / 100) * pos.management_fee_percentage * share;
    }
    const info2Parts = positions[0]?.info2?.split(";") ?? [];
    const perfFeeRateRaw = info2Parts[1] ?? "";
    const perfFeeRate =
      perfFeeRateRaw !== "" && !Number.isNaN(Number(perfFeeRateRaw))
        ? Number(perfFeeRateRaw)
        : null;
    const effectiveFeeRounded = r(effectiveFee);
    const totalManFeeRounded = r(totalManFee);
    const totalKickRounded = r(totalKick);
    const accountFeeRounded = r(accountFee);
    return {
      portCost: effectiveFeeRounded,
      portCostInclAccountFee: r(effectiveFeeRounded + accountFeeRounded),
      portClean: r(effectiveFeeRounded - totalKickRounded),
      portManFee: totalManFeeRounded,
      portManKick: totalKickRounded,
      portTotFee: r(
        effectiveFeeRounded +
          totalManFeeRounded +
          accountFeeRounded -
          totalKickRounded
      ),
      actAccountFee: accountFeeRounded,
      actPerfFee: perfFeeRate,
    };
  } else {
    const effectiveFeeRounded = r(effectiveFee);
    const accountFeeRounded = r(accountFee);
    return {
      portCost: effectiveFeeRounded,
      portCostInclAccountFee: r(effectiveFeeRounded + accountFeeRounded),
      portClean: effectiveFeeRounded,
      portManFee: 0,
      portManKick: 0,
      portTotFee: r(effectiveFeeRounded + accountFeeRounded),
      actAccountFee: accountFeeRounded,
      actPerfFee: null,
    };
  }
}

/** Reads a numeric value from the profile map. Returns null if absent or empty. */
function profileNum(profile: ProfileMap, key: string): number | null {
  // Direct lookup (legacy full key like "contact.profiling.aumStocks")
  const direct = profile[key];
  if (direct !== null && direct !== undefined && direct !== "")
    return Number(direct);

  // Typed-prefix variant: scan for any key ending with _<lastSegment>
  // e.g. "contact.profiling.double_aumStocks" matches key "contact.profiling.aumStocks"
  const lastDot = key.lastIndexOf(".");
  if (lastDot !== -1) {
    const ns = key.slice(0, lastDot + 1); // "contact.profiling."
    const name = key.slice(lastDot + 1); // "aumStocks"
    for (const k of Object.keys(profile)) {
      if (k.startsWith(ns) && k.endsWith(`_${name}`)) {
        const v = profile[k];
        if (v !== null && v !== undefined && v !== "") return Number(v);
      }
    }
  }
  return null;
}

function weightedTradeTag(
  tradeList: Array<{
    amountNok: number;
    value: number | null;
  }>
): number | null {
  const taggedTrades = tradeList.filter(
    (trade) => trade.value !== null && trade.amountNok > 0
  );
  const totalAmount = taggedTrades.reduce(
    (sum, trade) => sum + trade.amountNok,
    0
  );
  if (totalAmount === 0) return null;

  const weightedValue = taggedTrades.reduce(
    (sum, trade) => sum + (trade.value ?? 0) * trade.amountNok,
    0
  );
  return roundNumber(weightedValue / totalAmount, 2);
}

// ---------------------------------------------------------------------------
// Class
// ---------------------------------------------------------------------------

/** Financial investments the client holds outside Capguard, as entered in Scope B. */
export interface ScopeBExternalInvestments {
  equity: number;
  interest: number;
  cash: number;
  other: number;
}

// classType1 codes on a security that count as non-stock (cash / fixed income).
const NON_STOCK_SECURITY_CLASS_CODES = ["C", "F"];

type SuitabilityStatus =
  | "NOT_REQUESTED"
  | "INPUT_INCOMPLETE"
  | "AUTO_OK"
  | "MANUAL_SUITABILITY_REQUIRED"
  | "BLOCKED_BY_REVIEW";

export class AutoSuit {
  private readonly contact: Contact;
  private readonly profile: ProfileMap;
  private readonly testOverrides: TestOverrides;
  private readonly orders: SuitOrderInput[];
  private readonly pendingOrdersForFee: PendingOrder[];
  private readonly modelsData: QueryModelPosition[];
  /** §51 advice scope — "A" (within Capguard) or "B" (spans holdings outside Capguard). */
  private readonly adviceScope: "A" | "B";
  /** Whether the advisor manually overrode the automatic §51 classification. */
  private readonly manualOverride: boolean;
  /** Scope B: financial investments the client holds outside Capguard. */
  private readonly scopeB: ScopeBExternalInvestments;
  /** Active Capguard portfolios, used to list existing holdings untouched by this order. */
  private readonly activePorts: Portfolio[];

  private suitabilityStatus: SuitabilityStatus = "NOT_REQUESTED";
  private movementDirection: string | null = null;

  /**
   * Reads a profile value. Priority:
   *   1. testOverrides[key] or testOverrides["contact.cgProfiling." + key]
   *   2. profile[key]
   *   3. profile["contact.cgProfiling." + key]
   * Always returns a string -- empty string when absent.
   */
  private readonly p: (key: string) => string;

  constructor(
    profile: ProfileMap,
    testOverrides: TestOverrides,
    contact: Contact,
    orders: SuitOrderInput[] = [],
    pendingOrdersForFee: PendingOrder[] = [],
    modelsData: QueryModelPosition[] = [],
    adviceScope: "A" | "B" = "A",
    manualOverride = false,
    scopeB: ScopeBExternalInvestments = {
      equity: 0,
      interest: 0,
      cash: 0,
      other: 0,
    },
    activePorts: Portfolio[] = []
  ) {
    this.profile = profile;
    this.testOverrides = testOverrides;
    this.contact = contact;
    this.orders = orders;
    this.pendingOrdersForFee = pendingOrdersForFee;
    this.modelsData = modelsData;
    this.adviceScope = adviceScope;
    this.manualOverride = manualOverride;
    this.scopeB = scopeB;
    this.activePorts = activePorts;

    this.p = (key: string): string => {
      const full = `contact.cgProfiling.${key}`;

      // 1. testOverrides: exact short key, full cgProfiling key, or any <type>_<key> variant
      if (key in testOverrides && testOverrides[key] !== "") {
        return testOverrides[key];
      }
      if (full in testOverrides && testOverrides[full] !== "") {
        return testOverrides[full];
      }
      const typedOverrideKey = Object.keys(testOverrides).find(
        (k) => k.endsWith(`_${key}`) && testOverrides[k] !== ""
      );
      if (typedOverrideKey) return testOverrides[typedOverrideKey];

      // 2. typed-prefix key takes priority over legacy (new data beats old)
      const suffix = `_${key}`;
      for (const k of Object.keys(profile)) {
        if (k.startsWith("contact.cgProfiling.") && k.endsWith(suffix)) {
          const v = profile[k];
          if (v != null && v !== "") return String(v);
        }
      }

      // 3. legacy: short key or full cgProfiling key (fallback for old data)
      const direct = profile[key] ?? profile[full] ?? null;
      if (direct != null && direct !== "") return String(direct);

      return "";
    };
  }

  toData(): Partial<SuitStatementData> {
    const equityShareInOrders = this.equityShareInOrders();
    const fixedIncomeShareInOrders = this.fixedIncomeShareInOrders();

    return {
      date: dayjs().format("DD.MM.YYYY"),
      mifidSignDate: this.mifidSignDate(),
      clientName: this.contactName(),
      clientClassification: this.contactClassification(),
      investmentAmount: this.investmentAmount(),
      advisors: this.advisors(),
      equityShareBefore: this.equityShareBefore(),
      equityShareInOrders,
      equityShareAfter: this.equityShareAfter(),
      fixedIncomeShareBefore: this.fixedIncomeShareBefore(),
      fixedIncomeShareInOrders,
      fixedIncomeShareAfter: this.fixedIncomeShareAfter(),
      recommendedInvestmentStockPart: this.recommendedInvestmentStockPart(),
      recommendationShareAfter: this.recommendationShareAfter(),
      overallStockPartBefore: this.overallStockPartBefore(),
      overallStockPartAfter: this.overallStockPartAfter(),
      scopeBTotal: this.scopeBTotal(),
      scopeBEquityShare: this.scopeBEquityShare(),
      ordersInsideRecommendedRange: this.ordersInsideRecommendedRange(),
      recommendedMin: this.recommendedMin(),
      recommendedMax: this.recommendedMax(),
      recommendedEquityShareText: this.recommendedEquityShareText(),
      existingPortfolios: this.existingPortfolios(),
      withinRecommendedRange: this.withinRecommendedRange(),
      belowRecommendedRange: this.belowRecommendedRange(),
      aboveRecommendedRange: this.aboveRecommendedRange(),
      section51Variant: this.section51Variant(),
      scopeBEquityShareAfter: this.scopeBEquityShareAfter(),
      movementDirection: this.movementDirection,
      suitabilityStatus: this.suitabilityStatus,
      hasLimitedUnlistedExposure: this.hasLimitedUnlistedExposure(),
      orderList: this.orderList(),
      kostnaderEtableringPct: this.kostnaderEtableringPct(),
      kostnaderLopendePct: this.kostnaderLopendePct(),
      kostnaderLopendePctBreakdown: this.kostnaderLopendePctBreakdown(),
      kostnaderUttakPct: 0,
      liquidityBuffer: this.liquidityBuffer(),
      liquidInvestableWealth: this.liquidInvestableWealth(),
      totalNetWealth: this.totalNetWealth(),
      investmentvsNetWorthFlag: this.investmentVsNetWorthFlag(),
      investmentPurpose: this.investmentPurpose(),
      investmentHorizon: this.investmentHorizon(),
      investmentHorizonNumbers: this.investmentHorizonNumbers(),
      liquidationTimeNumbers: this.liquidationTimeNumbers(),
      hasDifferentInvestmentHorizon: this.hasDifferentInvestmentHorizon(),
      longestInvestmentHorizon: this.longestInvestmentHorizon(),
      riskTolerance: this.riskTolerance(),
      valueDropTolerance: this.valueDropTolerance(),
      liquidityNeeds: this.liquidityNeeds(),
      esgPreference: this.esgPreference(),
      esgPreferenceType: this.esgPreferenceType(),
      hasPerformanceFee: this.hasPerformanceFee(),
      docType: this.docType(),
    };
  }

  docType(): string | null {
    return this.contact.juridical.code === "Person" ? "PRIV" : "ORG";
  }

  contactName(): string | null {
    const formName = this.p("c.name");
    const name = this.contact.name;
    return formName || name;
  }

  contactClassification(): string | null {
    const classification = this.contact.classification?.namesAsMap;
    return getLocaleByMap(
      "no-NO",
      classification,
      this.contact.classification?.name || null
    );
  }

  mifidSignDate(): string | null {
    const signDate = this.p("mifidSignDate");
    return signDate !== "" ? dayjs(signDate).format("DD.MM.YYYY") : null;
  }

  investmentPurpose(): string | null {
    const purpose =
      this.p("mifidMainInvestmentPurpose") || this.p("mifidInvestmentGoal");
    return purpose !== "" ? purpose : null;
  }

  investmentHorizon(): string | null {
    const horizon =
      this.p("mifidEarliestWithdrawal") || this.p("mifidInvestmentHorizon");
    return horizon !== "" ? horizon : null;
  }

  investmentHorizonNumbers(): number[] {
    return Array.from(new Set(this.recommendedInvestmentHorizons())).sort(
      (a, b) => a - b
    );
  }

  liquidationTimeNumbers(): number[] {
    return Array.from(new Set(this.recommendedLiquidationTimes())).sort(
      (a, b) => a - b
    );
  }

  /**
   * Returns true when tagged products in the recommendation use more than one
   * investment horizon. Model orders use the model portfolio tag; instrument
   * orders use each security's tag.
   */
  hasDifferentInvestmentHorizon(): boolean {
    return new Set(this.recommendedInvestmentHorizons()).size > 1;
  }

  /** Returns the greatest investment-horizon tag among recommended products. */
  longestInvestmentHorizon(): number | null {
    const horizons = this.recommendedInvestmentHorizons();
    return horizons.length > 0 ? Math.max(...horizons) : null;
  }

  private recommendedInvestmentHorizons(): number[] {
    const horizons: number[] = [];
    const addHorizon = (tags: string | null | undefined) => {
      const horizon = this.getTagNumber(tags ?? "", investmentHorizonTag);
      if (horizon !== null) horizons.push(horizon);
    };

    if (this.pendingOrdersForFee.length > 0) {
      for (const order of this.pendingOrdersForFee) {
        const modelType = order.modelType ?? "";
        if (modelType && modelType !== "NOMODEL") {
          addHorizon(this.modelTags(modelType));
        } else {
          for (const row of order.manualOrderRows ?? []) {
            addHorizon(row.security?.tags);
          }
        }
      }
    } else {
      for (const order of this.orders) {
        if (order.kind === "model") {
          addHorizon(this.modelTags(order.modelCode));
        }
      }
    }

    return horizons;
  }

  private recommendedLiquidationTimes(): number[] {
    const liquidationTimes: number[] = [];
    const addLiquidationTime = (tags: string | null | undefined) => {
      const liquidationTime = this.getTagNumber(tags ?? "", liquidationTimeTag);
      if (liquidationTime !== null) liquidationTimes.push(liquidationTime);
    };

    if (this.pendingOrdersForFee.length > 0) {
      for (const order of this.pendingOrdersForFee) {
        const modelType = order.modelType ?? "";
        if (modelType && modelType !== "NOMODEL") {
          addLiquidationTime(this.modelTags(modelType));
        } else {
          for (const row of order.manualOrderRows ?? []) {
            addLiquidationTime(row.security?.tags);
          }
        }
      }
    } else {
      for (const order of this.orders) {
        if (order.kind === "model") {
          addLiquidationTime(this.modelTags(order.modelCode));
        }
      }
    }

    return liquidationTimes;
  }

  riskTolerance(): string | null {
    const tolerance = this.p("mifidRiskToleranceVolatility");
    return tolerance !== "" ? tolerance : null;
  }
  valueDropTolerance(): string | null {
    const tolerance = this.p("mifidValueDeclineImpact");
    return tolerance !== "" ? tolerance : null;
  }
  liquidityNeeds(): string | null {
    const needs = this.p("mifidLiquidityNeed");
    return needs !== "" ? needs : null;
  }
  esgPreference(): string | null {
    const pref = this.p("mifidEsgPreference");
    return pref !== "" ? pref : null;
  }
  esgPreferenceType(): string | null {
    const pref = this.p("mifidEsgPreferenceType");
    return pref !== "" ? pref : null;
  }

  orderList(): SuitStatementData["orderList"] {
    type OrderRow = NonNullable<SuitStatementData["orderList"]>[number];
    type FeeFields = Pick<
      OrderRow,
      | "portCost"
      | "portCostInclAccountFee"
      | "portClean"
      | "portManFee"
      | "portManKick"
      | "portTotFee"
      | "actAccountFee"
      | "actPerfFee"
      | "tegningskostPct"
      | "tegningskostBelop"
      | "tegningskostHvoravCapguardPct"
    >;
    const zeroFees: FeeFields = {
      portCost: 0,
      portCostInclAccountFee: 0,
      portClean: 0,
      portManFee: 0,
      portManKick: 0,
      portTotFee: 0,
      actAccountFee: 0,
      actPerfFee: null,
      tegningskostPct: 0,
      tegningskostBelop: 0,
      tegningskostHvoravCapguardPct: 0,
    };

    if (this.pendingOrdersForFee.length > 0) {
      return this.pendingOrdersForFee.map((order, i) => {
        const modelType = order.modelType ?? "";
        const isModel = !!modelType && modelType !== "NOMODEL";
        const stockFraction = isModel
          ? this.modelStockFraction(modelType)
          : order.stockFraction ?? 0;
        const amountToInvest =
          (order.amountToInvest ?? 0) +
          (order.oneOffAmount ?? 0) -
          (order.withdrawal ?? 0);
        const feeRow = computeOrderFeeRow(order, this.modelsData);
        const subscriptionPct = order.subscriptionFeePct ?? 0;
        const feeType = order.feeType ?? "";
        const instrumentNames = (order.instrumentNames ?? [])
          .map((n) => n.trim())
          .filter(Boolean)
          .join(", ");
        const modelName =
          (isModel
            ? resolveModelDescription(modelType, order.modelTypeName)
            : "") ||
          instrumentNames ||
          (order.portfolioName ?? "").trim() ||
          "Portefolje";
        const tradeList = (order.manualOrderRows ?? []).map((row) => ({
          name: row.instrumentName,
          amountNok: row.amountNok,
          accountFee: 0,
          management: null,
          investmentHorizon: this.getTagNumber(
            row.security?.tags ?? "",
            investmentHorizonTag
          ),
          liquidationTime: this.getTagNumber(
            row.security?.tags ?? "",
            liquidationTimeTag
          ),
        }));
        const weightedInvestmentHorizon = weightedTradeTag(
          tradeList.map((trade) => ({
            amountNok: trade.amountNok,
            value: trade.investmentHorizon,
          }))
        );
        const weightedLiquidationTime = weightedTradeTag(
          tradeList.map((trade) => ({
            amountNok: trade.amountNok,
            value: trade.liquidationTime,
          }))
        );
        return {
          index: i + 1,
          portfolioId: order.portfolioId,
          portfolioName: order.portfolioName ?? "",
          modelName,
          modelType,
          investmentHorizon: isModel
            ? this.getTagNumber(
                this.modelTags(modelType) ?? "",
                investmentHorizonTag
              )
            : weightedInvestmentHorizon,
          liquidationTime: isModel
            ? this.getTagNumber(
                this.modelTags(modelType) ?? "",
                liquidationTimeTag
              )
            : weightedLiquidationTime,
          amountToInvest,
          aksjeandel: stockFraction,
          ...feeRow,
          tegningskostPct: subscriptionPct,
          tegningskostBelop: (amountToInvest * subscriptionPct) / 100,
          tegningskostHvoravCapguardPct:
            feeType === "onlyFund" ? 0 : subscriptionPct,
          tradeList,
        };
      });
    }

    return this.orders.map((order, i) => {
      const stockFraction =
        order.kind === "model" ? this.modelStockFraction(order.modelCode) : 0;
      const amountToInvest =
        (order.amountToInvest ?? 0) +
        (order.oneOffAmount ?? 0) -
        (order.withdrawal ?? 0);
      const tradeList =
        order.kind === "instruments"
          ? order.buyOrders.map((b) => ({
              name: b.instrumentName,
              amountNok: b.amountNok,
              accountFee: 0,
              management: null,
              investmentHorizon: null,
              liquidationTime: null,
            }))
          : [];
      return {
        index: i + 1,
        portfolioId: order.portfolioId,
        portfolioName: order.portfolioName,
        modelName:
          order.kind === "model"
            ? order.modelName || this.modelDisplayName(order.modelCode)
            : "",
        modelType: order.kind === "model" ? order.modelCode : "",
        tags: order.kind === "model" ? this.modelTags(order.modelCode) : "",
        amountToInvest,
        aksjeandel: stockFraction,
        ...zeroFees,
        tradeList,
      };
    });
  }

  //Advisors
  advisors(): string | null {
    return (
      this.contact.representatives
        .filter((x) => x.contactId.startsWith("INV"))
        ?.map((r) => r.name)
        .filter((n) => n !== "")
        .join(", ") || null
    );
  }

  // Weighted average of a fee field across all orderList rows by amountToInvest.
  private weightedAvgFee(
    field:
      | "tegningskostPct"
      | "portTotFee"
      | "portManFee"
      | "actAccountFee"
      | "portCost"
      | "portManKick"
  ): number | null {
    const rows = this.orderList();
    if (!rows || rows.length === 0) return null;
    const totalAmt = rows.reduce((s, r) => s + r.amountToInvest, 0);
    if (totalAmt === 0) return null;
    const weighted = rows.reduce((s, r) => s + r[field] * r.amountToInvest, 0);
    return roundNumber(weighted / totalAmt, 4);
  }

  kostnaderEtableringPct(): number | null {
    const v = this.weightedAvgFee("tegningskostPct");
    return v !== null ? roundNumber(v / 100, 6) : null;
  }

  kostnaderLopendePct(): number | null {
    const v = this.weightedAvgFee("portTotFee");
    return v !== null ? roundNumber(v / 100, 6) : null;
  }

  // kostnaderLopendePct broken down into fund cost, account fee, management fee and kickback.
  kostnaderLopendePctBreakdown(): SuitStatementData["kostnaderLopendePctBreakdown"] {
    const toFraction = (v: number | null) =>
      v !== null ? roundNumber(v / 100, 6) : null;
    return {
      fundCostPct: toFraction(this.weightedAvgFee("portManFee")),
      accountFeePct: toFraction(this.weightedAvgFee("actAccountFee")),
      managementFeePct: toFraction(this.weightedAvgFee("portCost")),
      kickbackPct: toFraction(this.weightedAvgFee("portManKick")),
    };
  }

  investmentAmount(): number | null {
    const pre = this.p("_investmentAmount");
    if (pre !== "") return roundNumber(parseFloat(pre), 2);

    const totalOrderValue = this.orders.reduce((sum, order) => {
      const orderValue =
        (order.sumAvCash ?? 0) +
        (order.sumInsValue ?? 0) +
        (order.amountToInvest ?? 0) -
        (order.withdrawal ?? 0);
      return sum + (orderValue > 0 ? orderValue : 0);
    }, 0);

    return totalOrderValue > 0 ? roundNumber(totalOrderValue, 2) : null;
  }

  // Existing Capguard portfolio value (cash + holdings) before this order — no new money, no withdrawal.
  private existingPortfolioValue(): number {
    if (this.pendingOrdersForFee.length > 0) {
      return this.pendingOrdersForFee.reduce(
        (sum, o) => sum + (o.sumAvCash ?? 0) + (o.sumInsValue ?? 0),
        0
      );
    }
    return this.orders.reduce(
      (sum, o) => sum + (o.sumAvCash ?? 0) + (o.sumInsValue ?? 0),
      0
    );
  }

  // Ids of portfolios already covered by a queued order — excluded from existingPortfolios().
  private orderedPortfolioIds(): Set<string> {
    const ids = new Set<string>();
    for (const order of this.pendingOrdersForFee) {
      if (order.portfolioId) ids.add(String(order.portfolioId));
    }
    for (const order of this.orders) {
      if (order.portfolioId) ids.add(String(order.portfolioId));
    }
    return ids;
  }

  // Existing Capguard portfolios not part of this order — the rest of the total picture for the declaration.
  existingPortfolios(): SuitStatementData["existingPortfolios"] {
    const orderedIds = this.orderedPortfolioIds();
    return this.activePorts
      .filter((port) => !orderedIds.has(String(port.id)))
      .map((port) => {
        let itemTotal = 0;
        let stockValue = 0;
        for (const item of port.portfolioReport?.portfolioReportItems ?? []) {
          const mv = item.marketValue ?? 0;
          itemTotal += mv;
          if (
            !NON_STOCK_SECURITY_CLASS_CODES.includes(
              item.security?.classType1?.code ?? ""
            )
          ) {
            stockValue += mv;
          }
        }
        const cash = (port.portfolioReport?.marketValue ?? 0) - itemTotal;
        const marketValue = itemTotal + cash;
        const modelPortfolioName =
          port.modelPortfolio?.linkedPortfolio?.name ||
          port.modelPortfolio?.name ||
          null;
        return {
          portfolioId: String(port.id),
          portfolioName: port.name || port.shortName || "Portefølje",
          modelPortfolioName,
          marketValue: roundNumber(marketValue, 2),
          equityShare:
            marketValue > 0 ? roundNumber(stockValue / marketValue, 4) : null,
        };
      });
  }

  // Sum of the client's stated financial investments held outside Capguard (Scope B).
  scopeBTotal(): number {
    return (
      this.scopeB.equity +
      this.scopeB.interest +
      this.scopeB.cash +
      this.scopeB.other
    );
  }

  // Share of the Scope B total that is equity (0–1).
  scopeBEquityShare(): number | null {
    const total = this.scopeBTotal();
    return total > 0 ? roundNumber(this.scopeB.equity / total, 4) : null;
  }

  // Alias of equityShareInOrders() under the §51-report field name.
  recommendedInvestmentStockPart(): number | null {
    return this.equityShareInOrders();
  }

  // Share of the combined total (this investment + stated external investments) that this investment represents.
  recommendationShareAfter(): number | null {
    const investment = this.investmentAmount();
    if (investment === null) return null;
    const combined = investment + this.scopeBTotal();
    return combined > 0 ? roundNumber(investment / combined, 4) : null;
  }

  // Combined equity share across the existing Capguard portfolio + Scope B external investments, before this order.
  overallStockPartBefore(): number | null {
    const existingValue = this.existingPortfolioValue();
    const existingEquityShare = this.equityShareBefore();
    if (existingEquityShare === null) return null;
    const existingEquityValue = existingValue * existingEquityShare;
    const combinedTotal = existingValue + this.scopeBTotal();
    return combinedTotal > 0
      ? roundNumber(
          (existingEquityValue + this.scopeB.equity) / combinedTotal,
          4
        )
      : null;
  }

  // Share of equity in the recommended investment and Scope B external investments.
  // Use the order-only equity share here; equityShareAfter() may include the
  // existing Capguard portfolio and therefore describe a different base.
  scopeBEquityShareAfter(): number | null {
    const investmentAmt = this.investmentAmount();
    if (investmentAmt === null) return null;
    const investmentEquityShare = this.equityShareInOrders();
    if (investmentEquityShare === null) return null;
    const investmentEquityAmount = investmentAmt * investmentEquityShare;
    const scopeBTotal = this.scopeBTotal();
    const combinedTotal = investmentAmt + scopeBTotal;
    return combinedTotal > 0
      ? roundNumber(
          (investmentEquityAmount + this.scopeB.equity) / combinedTotal,
          4
        )
      : null;
  }

  // Combined equity share across the Capguard portfolio + Scope B external investments, after this order.
  overallStockPartAfter(): number | null {
    const afterValue = this.investmentAmount();
    const afterEquityShare = this.equityShareAfter();
    if (afterValue === null || afterEquityShare === null) return null;
    const afterEquityValue = afterValue * afterEquityShare;
    const combinedTotal = afterValue + this.scopeBTotal();
    return combinedTotal > 0
      ? roundNumber((afterEquityValue + this.scopeB.equity) / combinedTotal, 4)
      : null;
  }

  // Equity fraction (0–1) before any orders are applied.
  equityShareBefore(): number | null {
    const override = this.p("overrideCurrentEquityPct");
    if (override !== "") {
      return roundNumber(clampFraction(parseFloat(override) / 100), 4);
    }

    const pre = this.p("_stockCurrent");
    if (pre !== "") {
      return roundNumber(clampFraction(parseFloat(pre)), 4);
    }

    const aumPct = profileNum(this.profile, "contact.profiling.aumStocks");
    return aumPct !== null ? roundNumber(clampFraction(aumPct / 100), 4) : null;
  }

  // Equity fraction (0–1) represented by the active queued orders.
  equityShareInOrders(): number | null {
    let totalOrderValue = 0;
    let totalStockValue = 0;

    // Prefer pendingOrdersForFee (PendingOrder[]); fall back to SuitOrderInput orders.
    if (this.pendingOrdersForFee.length > 0) {
      for (const order of this.pendingOrdersForFee) {
        const orderValue =
          (order.sumAvCash ?? 0) +
          (order.sumInsValue ?? 0) +
          (order.amountToInvest ?? 0) -
          (order.withdrawal ?? 0);
        if (orderValue <= 0) continue;
        const stockFraction =
          order.modelType && order.modelType !== "NOMODEL"
            ? this.modelStockFraction(order.modelType)
            : order.stockFraction ?? 0;
        totalOrderValue += orderValue;
        totalStockValue += orderValue * stockFraction;
      }
    } else {
      for (const order of this.orders) {
        const orderValue =
          (order.sumAvCash ?? 0) +
          (order.sumInsValue ?? 0) +
          (order.amountToInvest ?? 0) -
          (order.withdrawal ?? 0);
        if (orderValue <= 0) continue;
        const stockFraction =
          order.kind === "model" ? this.modelStockFraction(order.modelCode) : 0;
        totalOrderValue += orderValue;
        totalStockValue += orderValue * stockFraction;
      }
    }

    return totalOrderValue > 0
      ? roundNumber(totalStockValue / totalOrderValue, 4)
      : null;
  }

  // Equity fraction (0–1) after orders are applied.
  equityShareAfter(): number | null {
    const fin = this.p("_stockFin");
    if (fin !== "") return roundNumber(parseFloat(fin), 4);
    return this.equityShareBefore();
  }

  fixedIncomeShareBefore(): number | null {
    const e = this.equityShareBefore();
    return e !== null ? roundNumber(1 - e, 4) : null;
  }

  fixedIncomeShareInOrders(): number | null {
    const e = this.equityShareInOrders();
    return e !== null ? roundNumber(1 - e, 4) : null;
  }

  fixedIncomeShareAfter(): number | null {
    const e = this.equityShareAfter();
    return e !== null ? roundNumber(1 - e, 4) : null;
  }

  private modelDisplayName(modelShortName: string): string {
    return modelShortName;
  }

  private modelStockFraction(modelShortName: string): number {
    const positions = this.modelsData.filter(
      (p) => p.short_name === modelShortName
    );
    if (positions.length === 0) return 0;

    const total = positions.reduce(
      (sum, p) => sum + (p.share_of_portfolio ?? 0),
      0
    );
    if (total === 0) return 0;

    const stockTotal = positions
      .filter((p) => !["1", "12"].includes(String(p.class_type1 ?? "")))
      .reduce((sum, p) => sum + (p.share_of_portfolio ?? 0), 0);

    return stockTotal / total;
  }

  // Tags of the model portfolio itself (same for all positions of a model).
  private modelTags(modelShortName: string): string {
    return (
      this.modelsData.find((p) => p.short_name === modelShortName)
        ?.portfolioTags ?? ""
    );
  }

  private getTagNumber(tags: string, tagName: string): number | null {
    const prefix = `${tagName}-`;
    const tag = tags
      .split(",")
      .map((value) => value.trim())
      .find((value) => value.startsWith(prefix));

    if (!tag) return null;

    const number = Number(tag.slice(prefix.length));
    return Number.isInteger(number) && number >= 0 ? number : null;
  }

  ordersInsideRecommendedRange(): boolean {
    const min = this.recommendedMin();
    const max = this.recommendedMax();
    const inOrders = this.equityShareInOrders();

    if (inOrders === null) return true;
    if (min !== null && inOrders < min) return false;
    if (max !== null && inOrders > max) return false;
    return true;
  }

  hasLimitedUnlistedExposure(): boolean | null {
    const LIMITED_UNLISTED_MODELS = ["CAPGUARDEKB", "CAPGUARDEIENDOM"];

    const pendingModels = this.pendingOrdersForFee.map(
      (o) => o.modelType ?? ""
    );
    if (this.pendingOrdersForFee.length > 0) {
      return pendingModels.some((m) => LIMITED_UNLISTED_MODELS.includes(m));
    }

    if (this.orders.length === 0) return null;
    return this.orders.some(
      (o) => o.kind === "model" && LIMITED_UNLISTED_MODELS.includes(o.modelCode)
    );
  }

  recommendedMin(): number | null {
    const override = this.p("overrideCGMinStockPart");
    if (override !== "") return roundNumber(parseFloat(override) / 100, 4);

    const recMin = this.p("CGMinStockPart");
    if (recMin !== "") return roundNumber(parseFloat(recMin) / 100, 4);

    const profileValue = profileNum(
      this.profile,
      "contact.cgProfiling.CGMinStockPart"
    );
    return profileValue !== null
      ? roundNumber(Math.min(profileValue, 100) / 100, 4)
      : null;
  }

  recommendedMax(): number | null {
    const override = this.p("overrideCGMaxStockPart");
    if (override !== "") return roundNumber(parseFloat(override) / 100, 4);

    const recMax = this.p("CGMaxStockPart");
    if (recMax !== "") return roundNumber(parseFloat(recMax) / 100, 4);

    const profileValue = profileNum(
      this.profile,
      "contact.cgProfiling.CGMaxStockPart"
    );
    return profileValue !== null
      ? roundNumber(Math.min(profileValue, 100) / 100, 4)
      : null;
  }

  recommendedEquityShareText(): string | null {
    const min = this.recommendedMin();
    const max = this.recommendedMax();
    if (min === null && max === null) return null;

    const minPct = min !== null ? Math.round(min * 100) : null;
    const maxPct = max !== null ? Math.round(max * 100) : null;

    if (minPct !== null && maxPct !== null) {
      return `${minPct}-${maxPct} %`;
    } else if (minPct !== null) {
      return `≥ ${minPct} %`;
    } else if (maxPct !== null) {
      return `≤ ${maxPct} %`;
    } else {
      return null;
    }
  }

  withinRecommendedRange(): boolean | null {
    const min = this.recommendedMin();
    const max = this.recommendedMax();
    const equity = this.equityShareInOrders();
    return (
      equity !== null &&
      (min === null || equity >= min) &&
      (max === null || equity <= max)
    );
  }

  belowRecommendedRange(): boolean | null {
    const min = this.recommendedMin();
    const equity = this.equityShareInOrders();
    return equity !== null && min !== null && equity < min;
  }

  aboveRecommendedRange(): boolean | null {
    const max = this.recommendedMax();
    const equity = this.equityShareInOrders();
    return equity !== null && max !== null && equity > max;
  }

  /**
   * §51-variant — which suitability narrative applies.
   * TODO: placeholder mapping only — exact thresholds/rules pending (see task notes).
   */
  section51Variant(): Section51Variant | null {
    if (this.adviceScope === "A") {
      const equity = this.equityShareInOrders();
      const min = this.recommendedMin();
      const max = this.recommendedMax();

      if (equity == null || min === null || max === null) {
        this.suitabilityStatus = "INPUT_INCOMPLETE";
        return null;
      } else if (equity < min || equity > max) {
        this.suitabilityStatus = "MANUAL_SUITABILITY_REQUIRED";
        return "A_MANUAL";
      } else {
        const position = getPositionInRange(equity, min, max);
        this.suitabilityStatus = "AUTO_OK";
        return `A_${position}`;
      }
    } else if (this.adviceScope === "B") {
      const recommendedInvestmentStockPart =
        this.recommendedInvestmentStockPart();
      const recommendationShareAfter = this.recommendationShareAfter();
      const overallStockPartBefore = this.overallStockPartBefore();
      const overallStockPartAfter = this.overallStockPartAfter();
      const minStockPart = this.recommendedMin();
      const maxStockPart = this.recommendedMax();
      if (
        minStockPart === null ||
        maxStockPart === null ||
        recommendedInvestmentStockPart === null ||
        recommendationShareAfter === null ||
        overallStockPartBefore === null ||
        overallStockPartAfter === null
      ) {
        this.suitabilityStatus = "INPUT_INCOMPLETE";
        return null;
      } else {
        const beforeDistance = distanceToRange(
          overallStockPartBefore,
          minStockPart,
          maxStockPart
        );
        const afterDistance = distanceToRange(
          overallStockPartAfter,
          minStockPart,
          maxStockPart
        );
        const afterInRange = afterDistance <= Number.EPSILON;
        const sameSideAbove =
          overallStockPartBefore > maxStockPart &&
          overallStockPartAfter > maxStockPart;
        const sameSideBelow =
          overallStockPartBefore < minStockPart &&
          overallStockPartAfter < minStockPart;
        const movesTowardRange =
          (sameSideAbove || sameSideBelow) &&
          afterDistance < beforeDistance - Number.EPSILON;
        if (afterInRange) {
          this.suitabilityStatus = "AUTO_OK";
          return "B_WITHIN_RANGE";
        }
        if (movesTowardRange) {
          this.movementDirection =
            overallStockPartAfter > maxStockPart ? "over" : "under";
          this.suitabilityStatus = "AUTO_OK";
          return "B_MOVES_TOWARD_RANGE";
        } else {
          this.suitabilityStatus = "MANUAL_SUITABILITY_REQUIRED";
          return "B_MANUAL";
        }
      }
    }
    return null;
  }

  liquidityBuffer(): number | null {
    const value = Number(this.p("mifidLiquidityBuffer"));
    return isNaN(value) ? null : value;
  }

  liquidInvestableWealth(): number | null {
    const value = Number(this.p("mifidLiquidInvestableWealth"));
    return isNaN(value) ? null : value;
  }

  totalNetWealth(): number | null {
    const value = Number(this.p("mifidTotalNetWealth"));
    return isNaN(value) ? null : value;
  }

  investmentVsNetWorthFlag(): boolean {
    const investment = this.investmentAmount() ?? 0;
    const liquid = this.liquidInvestableWealth() ?? 0;
    const needs = this.liquidityBuffer() ?? 0;
    return investment + needs > liquid;
  }

  hasPerformanceFee(): boolean {
    return !!this.orderList()?.some(
      (x) => x.actPerfFee != null && x.actPerfFee > 0
    );
  }
}

function getLocaleByMap<T>(
  locale: string,
  map: Record<string, T>,
  value: T
): T {
  const loc = locale.replaceAll("-", "_");
  return Object.prototype.hasOwnProperty.call(map, loc) ? map[loc] : value;
}

const distanceToRange = (
  value: number,
  minValue: number,
  maxValue: number
): number => {
  if (value < minValue) return minValue - value;
  if (value > maxValue) return value - maxValue;
  return 0;
};

type PositionInRange = "LOWER" | "MIDDLE" | "UPPER";

const getPositionInRange = (
  value: number,
  minValue: number,
  maxValue: number
): PositionInRange => {
  const width = maxValue - minValue;

  if (width <= 0) {
    return "MIDDLE";
  }

  const relative = (value - minValue) / width;

  if (relative <= 1 / 3) {
    return "LOWER";
  }

  if (relative <= 2 / 3) {
    return "MIDDLE";
  }

  return "UPPER";
};
